import React from 'react';
import { Plus, Type, AlignLeft, AlignCenter, AlignRight, Bold, Italic, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Slider } from '../ui/slider';

const FONTS = [
  { value: "'Space Grotesk', sans-serif", label: 'Space Grotesk' },
  { value: "Georgia, serif", label: 'Georgia' },
  { value: "'Courier New', monospace", label: 'Courier' },
  { value: "Impact, sans-serif", label: 'Impact' },
  { value: "'Trebuchet MS', sans-serif", label: 'Trebuchet' },
  { value: "'Palatino', serif", label: 'Palatino' },
];

const TEXT_COLORS = [
  '#ffffff', '#000000', '#1F467C', '#3980C3', '#5CB85C', '#ef4444',
  '#f59e0b', '#10b981', '#8b5cf6', '#ec4899', '#64748b', '#0f172a',
];

export function TextOverlay({ elements, selectedId, onAddText, onUpdateElement, onDeleteElement }) {
  const selectedEl = elements.find(el => el.id === selectedId && el.type === 'text');

  const addNewText = (preset) => {
    const base = {
      id: `text-${Date.now()}`,
      type: 'text',
      text: 'Your text here',
      x: 140,
      y: 440,
      width: 800,
      fontSize: 48,
      fontFamily: "'Space Grotesk', sans-serif",
      fill: '#ffffff',
      fontStyle: 'bold',
      align: 'center',
    };

    if (preset === 'heading') {
      onAddText({ ...base, text: 'HEADING', fontSize: 72, fontStyle: 'bold' });
    } else if (preset === 'subtext') {
      onAddText({ ...base, text: 'Subtitle text goes here', fontSize: 32, y: 520, fontStyle: 'normal' });
    } else if (preset === 'caption') {
      onAddText({ ...base, text: 'Caption text', fontSize: 24, y: 900, fontStyle: 'normal', align: 'left', x: 60 });
    } else {
      onAddText(base);
    }
  };

  return (
    <div className="space-y-5" data-testid="text-overlay-controls">
      {/* Add Text Buttons */}
      <div>
        <label className="text-xs font-bold uppercase tracking-wider text-[#64748b] mb-2 block">Add Text</label>
        <div className="grid grid-cols-3 gap-2">
          <Button variant="outline" size="sm" onClick={() => addNewText('heading')} className="border-[#e2e8f0] text-xs" data-testid="add-heading-btn">
            <Type className="w-3.5 h-3.5 mr-1" /> Heading
          </Button>
          <Button variant="outline" size="sm" onClick={() => addNewText('subtext')} className="border-[#e2e8f0] text-xs" data-testid="add-subtext-btn">
            <Type className="w-3 h-3 mr-1" /> Subtext
          </Button>
          <Button variant="outline" size="sm" onClick={() => addNewText('caption')} className="border-[#e2e8f0] text-xs" data-testid="add-caption-btn">
            <Type className="w-2.5 h-2.5 mr-1" /> Caption
          </Button>
        </div>
      </div>

      {/* Edit Selected Text */}
      {selectedEl && (
        <div className="space-y-4 pt-2 border-t border-[#e2e8f0]">
          <div className="flex items-center justify-between">
            <label className="text-xs font-bold uppercase tracking-wider text-[#2563eb]">Edit Text</label>
            <Button variant="ghost" size="sm" onClick={() => onDeleteElement(selectedEl.id)} className="text-red-400 hover:text-red-600 h-7 px-2" data-testid="delete-text-btn">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>

          {/* Text Content */}
          <Textarea
            value={selectedEl.text}
            onChange={(e) => onUpdateElement(selectedEl.id, { text: e.target.value })}
            rows={2}
            className="border-[#e2e8f0] text-sm resize-none"
            data-testid="text-content-input"
          />

          {/* Font */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-[10px] text-[#94a3b8] uppercase tracking-wider">Font</span>
              <Select value={selectedEl.fontFamily} onValueChange={(v) => onUpdateElement(selectedEl.id, { fontFamily: v })}>
                <SelectTrigger className="border-[#e2e8f0] h-8 text-xs" data-testid="font-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FONTS.map(f => (
                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <span className="text-[10px] text-[#94a3b8] uppercase tracking-wider">Size</span>
              <Slider
                value={[selectedEl.fontSize]}
                onValueChange={([v]) => onUpdateElement(selectedEl.id, { fontSize: v })}
                min={12} max={120} step={1}
                className="mt-2"
                data-testid="font-size-slider"
              />
              <span className="text-[10px] text-[#94a3b8]">{selectedEl.fontSize}px</span>
            </div>
          </div>

          {/* Style Buttons */}
          <div className="flex gap-1.5">
            <Button
              variant="outline" size="sm"
              onClick={() => onUpdateElement(selectedEl.id, { fontStyle: selectedEl.fontStyle === 'bold' ? 'normal' : 'bold' })}
              className={`h-8 w-8 p-0 border-[#e2e8f0] ${selectedEl.fontStyle === 'bold' ? 'bg-[#eff6ff] border-[#2563eb] text-[#2563eb]' : ''}`}
              data-testid="bold-btn"
            >
              <Bold className="w-3.5 h-3.5" />
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={() => onUpdateElement(selectedEl.id, { fontStyle: selectedEl.fontStyle === 'italic' ? 'normal' : 'italic' })}
              className={`h-8 w-8 p-0 border-[#e2e8f0] ${selectedEl.fontStyle === 'italic' ? 'bg-[#eff6ff] border-[#2563eb] text-[#2563eb]' : ''}`}
              data-testid="italic-btn"
            >
              <Italic className="w-3.5 h-3.5" />
            </Button>
            <div className="w-px bg-[#e2e8f0] mx-1" />
            {['left', 'center', 'right'].map(align => (
              <Button
                key={align}
                variant="outline" size="sm"
                onClick={() => onUpdateElement(selectedEl.id, { align })}
                className={`h-8 w-8 p-0 border-[#e2e8f0] ${selectedEl.align === align ? 'bg-[#eff6ff] border-[#2563eb] text-[#2563eb]' : ''}`}
                data-testid={`align-${align}-btn`}
              >
                {align === 'left' ? <AlignLeft className="w-3.5 h-3.5" /> : align === 'center' ? <AlignCenter className="w-3.5 h-3.5" /> : <AlignRight className="w-3.5 h-3.5" />}
              </Button>
            ))}
          </div>

          {/* Color */}
          <div>
            <span className="text-[10px] text-[#94a3b8] uppercase tracking-wider block mb-1.5">Color</span>
            <div className="flex flex-wrap gap-1.5">
              {TEXT_COLORS.map(color => (
                <button
                  key={color}
                  onClick={() => onUpdateElement(selectedEl.id, { fill: color })}
                  className={`w-7 h-7 rounded-lg border-2 transition-transform hover:scale-110 ${
                    selectedEl.fill === color ? 'border-[#2563eb] scale-110' : 'border-[#e2e8f0]'
                  }`}
                  style={{ backgroundColor: color }}
                  data-testid={`color-${color.replace('#', '')}`}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {!selectedEl && elements.filter(e => e.type === 'text').length > 0 && (
        <p className="text-xs text-[#94a3b8] italic">Click a text element on the canvas to edit it</p>
      )}
    </div>
  );
}
